import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Alert,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  getRedditJobs,
  createRedditJob,
  updateRedditJob,
  deleteRedditJob,
  getRedditJobHistory,
  getServerTimezone,
} from '../utils/api';

interface RedditJob {
  id: number;
  name: string;
  subreddit: string;
  post_limit: number;
  schedule: string;
  timezone: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface RedditJobHistory {
  id: number;
  job: number;
  job_name: string;
  status: 'SUCCESS' | 'FAILURE' | 'RUNNING';
  posts_fetched: number;
  posts_inserted: number;
  started_at: string;
  finished_at: string | null;
  duration: string | null;
  log: string;
}

const timezones = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai',
  'Asia/Kolkata', 'Australia/Sydney',
];

const RedditJobManagement: React.FC = () => {
  const [jobs, setJobs] = useState<RedditJob[]>([]);
  const [history, setHistory] = useState<RedditJobHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Server timezone info
  const [serverTimezone, setServerTimezone] = useState('UTC');
  const [serverTimeInfo, setServerTimeInfo] = useState<{
    server_timezone: string;
    server_timezone_name: string;
    current_server_time_formatted: string;
  } | null>(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<RedditJob | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subreddit: '',
    post_limit: 100,
    schedule: '0 5 * * *',
    timezone: 'UTC',
    is_enabled: true,
  });

  useEffect(() => {
    fetchJobs();
    fetchServerTimezone();
  }, []);

  const fetchServerTimezone = async () => {
    try {
      const response = await getServerTimezone();
      if (response.ok) {
        const data = await response.json();
        setServerTimezone(data.server_timezone);
        setServerTimeInfo(data);
        // Update default form timezone to match server
        setFormData(prev => ({ ...prev, timezone: data.server_timezone }));
      }
    } catch (err: any) {
      console.warn('Could not fetch server timezone:', err.message);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await getRedditJobs();
      if (response.ok) {
        const data = await response.json();
        setJobs(data.results || data);
      } else {
        throw new Error('Failed to fetch jobs');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobHistory = async (jobId: number) => {
    try {
      const response = await getRedditJobHistory(jobId);
      if (response.ok) {
        const data = await response.json();
        setHistory(data.results || data);
        setSelectedJobId(jobId);
        setHistoryDialogOpen(true);
      } else {
        throw new Error('Failed to fetch job history');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setFormData({
      name: '',
      subreddit: '',
      post_limit: 100,
      schedule: '0 5 * * *',
      timezone: serverTimezone,
      is_enabled: true,
    });
    setDialogOpen(true);
  };

  const handleEditJob = (job: RedditJob) => {
    setEditingJob(job);
    setFormData({
      name: job.name,
      subreddit: job.subreddit,
      post_limit: job.post_limit,
      schedule: job.schedule,
      timezone: job.timezone,
      is_enabled: job.is_enabled,
    });
    setDialogOpen(true);
  };

  const handleDeleteJob = async (jobId: number) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      const response = await deleteRedditJob(jobId);
      if (response.ok) {
        setSuccess('Job deleted successfully');
        fetchJobs();
      } else {
        throw new Error('Failed to delete job');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSubmit = async () => {
    try {
      let response;
      if (editingJob) {
        response = await updateRedditJob(editingJob.id, formData);
      } else {
        response = await createRedditJob(formData);
      }

      if (response.ok) {
        setSuccess(editingJob ? 'Job updated successfully' : 'Job created successfully');
        setDialogOpen(false);
        fetchJobs();
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          // Response is not valid JSON, use status text
          throw new Error(`Failed to save job: ${response.status} ${response.statusText}`);
        }
        
        // Parse Django REST Framework validation errors
        let errorMessage = 'Failed to save job';
        
        // Map field names to user-friendly labels
        const fieldLabels: { [key: string]: string } = {
          'name': 'Job Name',
          'subreddit': 'Subreddit',
          'post_limit': 'Post Limit',
          'schedule': 'Schedule', 
          'timezone': 'Timezone',
          'is_enabled': 'Enabled Status'
        };
        
        if (errorData && typeof errorData === 'object') {
          // Check for field-specific errors
          const fieldErrors = [];
          for (const [field, messages] of Object.entries(errorData)) {
            // Skip non-error fields like 'detail' and 'error' - handle them separately
            if (field === 'detail' || field === 'error') continue;
            
            if (Array.isArray(messages)) {
              const fieldLabel = fieldLabels[field] || field.charAt(0).toUpperCase() + field.slice(1);
              fieldErrors.push(`${fieldLabel}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              const fieldLabel = fieldLabels[field] || field.charAt(0).toUpperCase() + field.slice(1);
              fieldErrors.push(`${fieldLabel}: ${messages}`);
            }
          }
          
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join('\n');
          } else if (errorData.detail) {
            // Generic error detail
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            // Custom error field
            errorMessage = errorData.error;
          }
        }
        
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS': return 'success';
      case 'FAILURE': return 'error';
      case 'RUNNING': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5">Crawler Jobs</Typography>
          {serverTimeInfo && (
            <Typography variant="caption" color="text.secondary">
              Server timezone: {serverTimeInfo.server_timezone_name} • {serverTimeInfo.current_server_time_formatted}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateJob}
        >
          New Job
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subreddit</TableCell>
              <TableCell>Post Limit</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Timezone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id}>
                <TableCell>{job.name}</TableCell>
                <TableCell>/r/{job.subreddit}</TableCell>
                <TableCell>{job.post_limit}</TableCell>
                <TableCell>
                  <Tooltip title="Cron expression">
                    <code>{job.schedule}</code>
                  </Tooltip>
                </TableCell>
                <TableCell>{job.timezone}</TableCell>
                <TableCell>
                  <Chip
                    label={job.is_enabled ? 'Enabled' : 'Disabled'}
                    color={job.is_enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDateTime(job.created_at)}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEditJob(job)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => fetchJobHistory(job.id)} size="small">
                    <HistoryIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteJob(job.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {jobs.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No jobs found. Create your first job to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Job Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingJob ? 'Edit Job' : 'Create New Job'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Job Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Subreddit (without /r/)"
              value={formData.subreddit}
              onChange={(e) => handleInputChange('subreddit', e.target.value)}
              margin="normal"
              required
              placeholder="e.g., dataengineering"
            />
            <TextField
              type="number"
              label="Post Limit"
              value={formData.post_limit}
              onChange={(e) => handleInputChange('post_limit', parseInt(e.target.value, 10) || 100)}
              margin="normal"
              required
              inputProps={{ min: 1, max: 1000 }}
              helperText="Number of posts to fetch per execution (1-1000)"
            />
            <TextField
              fullWidth
              label="Schedule (Cron Expression)"
              value={formData.schedule}
              onChange={(e) => handleInputChange('schedule', e.target.value)}
              margin="normal"
              required
              helperText="e.g., '0 5 * * *' for daily at 5 AM"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Timezone</InputLabel>
              <Select
                value={formData.timezone}
                label="Timezone"
                onChange={(e) => handleInputChange('timezone', e.target.value)}
              >
                {timezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                    {tz === serverTimezone && ' (Server Default)'}
                  </MenuItem>
                ))}
              </Select>
              {serverTimeInfo && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: '#f0f8ff', borderRadius: 1 }}>
                  <Typography variant="caption" color="primary">
                    🌍 Server Environment: {serverTimeInfo.server_timezone_name}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Current server time: {serverTimeInfo.current_server_time_formatted}
                  </Typography>
                </Box>
              )}
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_enabled}
                  onChange={(e) => handleInputChange('is_enabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingJob ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Job History
          {selectedJobId && ` - ${jobs.find(j => j.id === selectedJobId)?.name}`}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Posts Fetched</TableCell>
                  <TableCell>Posts Inserted</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Finished At</TableCell>
                  <TableCell>Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.posts_fetched}</TableCell>
                    <TableCell>{item.posts_inserted}</TableCell>
                    <TableCell>{formatDateTime(item.started_at)}</TableCell>
                    <TableCell>
                      {item.finished_at ? formatDateTime(item.finished_at) : '-'}
                    </TableCell>
                    <TableCell>{item.duration || '-'}</TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No execution history found for this job.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedditJobManagement; 